import {createActions} from 'redux-actions';
import _ from 'lodash';

import api from '@api';
import utils from '@utils';

const actions = createActions({
  updateCurrentNotifications: async (
    input: NotificationsGetPagingQueryParams
  ) => {
    try {
      const res = await api.notifications.paging(input);

      const pagination: PaginationActionOutput = {
        data: res.data.pages,
        totalCount: res.headers['x-total-count']
          ? Number(res.headers['x-total-count'])
          : 0,
        totalPages: res.headers['x-total-pages']
          ? Number(res.headers['x-total-pages'])
          : 0,
        current: input.page,
        sort: input.sort,
      };

      return pagination;
    } catch (err) {
      throw new Error(err);
    }
  },

  updateCurrentNotificationDetail: async (
    input: NotificationsGetDetailPagingParams
  ) => {
    try {
      if (!input.notificationId) {
        const pagination: PaginationActionOutput = {
          data: {
            active: [],
            afterDistant: [],
            afterNear: [],
            beforeDistant: [],
            beforeNear: [],
            first: [],
            last: [],
          },
          totalCount: 0,
          totalPages: 0,
          current: input.page,
          sort: input.sort,
        };

        return pagination;
      }
      const res = await api.notifications.detailPaging(input);

      const pagination: PaginationActionOutput = {
        data: res.data.pages,
        totalCount: res.headers['x-total-count']
          ? Number(res.headers['x-total-count'])
          : 0,
        totalPages: res.headers['x-total-pages']
          ? Number(res.headers['x-total-pages'])
          : 0,
        current: input.page,
        sort: input.sort,
      };

      return pagination;
    } catch (err) {
      throw new Error(err);
    }
  },

  updateSearchWordsNotifications: async (
    keywords: NotificationsStore['search'],
    teamId: number
  ) => {
    try {
      let fromSentDatetime: any;
      let toSentDatetime: any;

      if (keywords.fromSentDatetime) {
        fromSentDatetime = utils.date.format(
          keywords.fromSentDatetime,
          'YYYY-MM-DD'
        );
      }
      if (keywords.toSentDatetime) {
        toSentDatetime = utils.date.format(
          keywords.toSentDatetime,
          'YYYY-MM-DD',
          true
        );
      }

      const res = await api.notifications.paging({
        ...keywords,
        page: 1,
        sort: '-sentDatetime',
        teamId,
        fromSentDatetime,
        toSentDatetime,
      });

      const pagination: PaginationActionOutput = {
        data: res.data.pages,
        totalCount: res.headers['x-total-count']
          ? Number(res.headers['x-total-count'])
          : 0,
        totalPages: res.headers['x-total-pages']
          ? Number(res.headers['x-total-pages'])
          : 0,
        current: 1,
        sort: '-sentDatetime',
      };

      return {pagination, keywords};
    } catch (err) {
      throw new Error(err);
    }
  },

  clearSearchWords: () => {
    return {
      fromSentDatetime: '',
      toSentDatetime: '',
      disasterType: null,
      prefecture: null,
    };
  },
  updateSearchTime: (searchTime: string) => {
    return {
      recentSearchDatetime: searchTime,
    };
  },
  getOneNotification: async (qp: NotificationGetOneQueryParams) => {
    const detail = await api.notifications.getOne(qp);
    const pagination = {
      data: {
        active: [],
        after_distant: [],
        after_near: [],
        before_distant: [],
        before_near: [],
        first: [],
        last: [],
      },
      sort: '+lastKana',
      current: 1,
      totalCount: 0,
      totalPages: 0,
      disabled: false,
    };

    return {detail, pagination};
  },

  getSummarizedNotification: async (qp: NotificationGetSummaryQueryParams) => {
    const detail = await api.notifications.getSummary(qp);

    const pagination = {
      data: {
        active: [],
        after_distant: [],
        after_near: [],
        before_distant: [],
        before_near: [],
        first: [],
        last: [],
      },
      sort: '+lastKana',
      current: 1,
      totalCount: 0,
      totalPages: 0,
      disabled: false,
    };

    return {detail, pagination};
  },

  getNotificationDetail: (id: string, notifications: NotificationsStore) => {
    const targetNotification = _.filter(notifications, (notification: any) => {
      return notification.id === id;
    });
    const detail = targetNotification[0];

    return {detail};
  },

  clearTableSort: () => {
    return {
      sort: '+lastKana',
    };
  },

  clearDetailSort: () => {
    return {
      sort: '-replyDatetime',
    };
  },

  clearDetailSearch: () => {
    return {
      names: [],
      isSafe: false,
      isMinorInjury: false,
      isSeriousInjury: false,
    };
  },

  updateDetailSearchWords: (
    keywords: NotificationsStore['detail']['search']
  ) => {
    return {
      names: keywords.names,
      organizations: keywords.organizations,
      positions: keywords.organizations,
      isAnswered: keywords.isAnswered,
      isSafe: keywords.isSafe,
      isMinorInjury: keywords.isMinorInjury,
      isSeriousInjury: keywords.isSeriousInjury,
    };
  },
  getDetailAnswer: async (
    id: string,
    answers: any[],
    notificationId: string,
    me: MeStore,
    sentDateTime: string
  ) => {
    const targetNotification = _.filter(answers, (answer: any) => {
      return answer.id === id;
    });
    const detail = targetNotification[0];

    if (_.isUndefined(detail)) {
      const res = await api.notifications.getOneAnswer(id);

      const notificationDetail = await api.notifications.getOne({
        teamId: me.teamId,
        notificationId,
      });
      return {...res.data, ...notificationDetail.data};
    }

    return {...detail, sentDateTime};
  },
  updateActiveNotifications: (
    pages: any,
    current: number,
    nextCurrent: number,
    totalPages: number
  ) => {
    const active = utils.pagination.makeRemoteActive(
      pages,
      current,
      nextCurrent,
      totalPages
    );

    return {active, nextCurrent};
  },
  updateActiveNotificationDetails: (
    pages: any,
    current: number,
    nextCurrent: number,
    totalPages: number
  ) => {
    const active = utils.pagination.makeRemoteActive(
      pages,
      current,
      nextCurrent,
      totalPages
    );

    return {active, nextCurrent};
  },
});

export default actions;
